import { useState, useEffect } from 'react';
import { useParams, Link, useLocation } from 'react-router-dom';
import '../../style/scss/Recruit.scss';
import BreadCrumb from "../Controls/BreadCrumb";
import Helpers from '../Controls/helpers';
import Spinner from '../Controls/Spinner';
import API from '@src/API.js';
import { MdAddBox } from "react-icons/md";
import { MdCheckBoxOutlineBlank, MdOutlineCheckBox } from "react-icons/md";
import { toast } from "react-toastify";

export default function EmployerAdminEdit() {
  const [admin, Update] = useState({ Admins: [] });
  const [breadcrumb, UpdateBreadcrumb] = useState([]);
  const [processing, setProcessing] = useState(false);
  const location = useLocation();
  const params = useParams();
  const employerID = parseInt(params.id || "0", 10);

  useEffect(() => {
    if (!employerID) return;
    let cancelled = false;
    const load = async () => {
      setProcessing(true);
      try {
        const resp = await API.getAsync('/Employer/' + employerID + '/Admin');
        if (cancelled) return;
        if (resp && resp.ok) {
          Update(resp.jsonData);
          UpdateBreadcrumb([
            ...((location.state || {}).breadcrumb || []),
            {
              label: (resp.jsonData.EmployerName || 'Employer') + ': Admin',
              route: '/employerAdminEdit/' + resp.jsonData.EmployerID
            }
          ]);
        } else {
          toast.error("Could not load employer admin data");
        }
      } catch (err) {
        console.error(err);
        toast.error("Could not load employer admin data");
      } finally {
        if (!cancelled) setProcessing(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [employerID, location.state]);

  const Toggle = async (e) => {
    setProcessing(true);
    const admins = [...(admin.Admins || [])];
    const person = Helpers.findWithAttr(admins, "PersonID", parseInt(e.currentTarget.dataset.personid, 10));
    if (!person || person.PersonID < 0) {
      setProcessing(false);
      return;
    }
    const roleID = parseInt(e.currentTarget.dataset.roleid, 10);
    const i = person.Roles.indexOf(roleID);
    if (i >= 0) person.Roles.splice(i, 1);
    else person.Roles.push(roleID);

    Update({ ...admin, Admins: admins });
    const payload = { ...person, EmployerID: employerID };
    try {
      const resp = await API.postAsync("/PostEmployerAdmin", payload);
      if (resp && resp.ok) toast.success("User rights updated");
      else toast.error("Could not update user rights");
    } catch (err) {
      console.error(err);
      toast.error("Could not update user rights");
    } finally {
      setProcessing(false);
    }
  };

  const ToggleMustChange = async (e) => {
    e?.stopPropagation?.();
    const personId = parseInt(e.currentTarget.dataset.personid, 10);
    const admins = [...(admin.Admins || [])];
    const person = Helpers.findWithAttr(admins, "PersonID", personId);
    if (!person || person.PersonID < 0) return;

    const next = !(person.MustChangePassword ?? person.mustChangePassword);
    person.MustChangePassword = next;
    Update({ ...admin, Admins: admins });
    setProcessing(true);
    try {
      const resp = await API.postAsync("/Person/MustChangePassword", {
        personId,
        mustChangePassword: next,
      });
      if (resp && resp.ok) toast.success(next ? "Password change required on next login" : "Requirement cleared");
      else {
        person.MustChangePassword = !next;
        Update({ ...admin, Admins: [...admins] });
        toast.error(resp?.jsonData?.message || "Could not update password requirement");
      }
    } catch (err) {
      console.error(err);
      person.MustChangePassword = !next;
      Update({ ...admin, Admins: [...admins] });
      toast.error("Could not update password requirement");
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div className="pagebody">
      <BreadCrumb props={breadcrumb} />
      <div className="tabbody">
        <h2>Admin: {admin.EmployerName}</h2>
        <div className="tabsection">
          <div className="listTitle">
            <span>Employer Admins / Rights</span>
            <div className="listTitleIcons">
              <Link
                to="/addEmployerVIP"
                state={{ EmployerID: employerID, breadcrumb }}
              >
                <MdAddBox />
              </Link>
            </div>
          </div>
          <div className="sectionData">
            <table>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Admin</th>
                  <th>HR</th>
                  <th>Recruiter</th>
                  <th>Require PW change</th>
                </tr>
              </thead>
              <tbody>
                {(admin.Admins || []).map(a => (
                  <tr key={a.PersonID}>
                    <td>{a.FirstName}&nbsp;{a.LastName}</td>
                    <td>{a.Email}</td>
                    {[20, 30, 50].map(roleId => (
                      <td key={`${a.PersonID}-${roleId}`} data-personid={a.PersonID} data-roleid={roleId} onClick={Toggle}>
                        {a.Roles.find(id => id === roleId) ? (
                          <MdOutlineCheckBox size={1} color="#0075FF" />
                        ) : (
                          <MdCheckBoxOutlineBlank size={1} color="#0075FF" />
                        )}
                      </td>
                    ))}
                    <td
                      data-testid={`must-change-password-${a.PersonID}`}
                      data-personid={a.PersonID}
                      onClick={ToggleMustChange}
                      title="Require password change on next login"
                      style={{ cursor: "pointer" }}
                    >
                      {(a.MustChangePassword ?? a.mustChangePassword) ? (
                        <MdOutlineCheckBox size={1} color="#0075FF" />
                      ) : (
                        <MdCheckBoxOutlineBlank size={1} color="#0075FF" />
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        <Link
          to={'/employerRecruitEdit/' + employerID}
          state={{ breadcrumb }}
        >
          Recruit
        </Link>
      </div>
      <Spinner show={processing} />
    </div>
  );
}
